import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaArrowRight, FaBook, FaUsers, FaRupeeSign, FaImage, FaClock } from 'react-icons/fa';
import { sendApiRequest } from '../utils/api';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const AddExam = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    examName: '',
    description: '',
    category: '',
    batchType: 'Regular Batch',
    examFee: '',
    timingPeriod: '',
    thumbnail: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await sendApiRequest('api/exam-categories');
        if (response) {
          setCategories(response);
        } else {
          throw new Error('Failed to fetch categories');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to load exam categories',
          confirmButtonColor: '#ef4444'
        });
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    fetchCategories();
  }, []);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );
    return response.json();
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setPreviewImage(URL.createObjectURL(files[0]));
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validate form data
      if (!formData.examName || !formData.description || !formData.examFee || 
          !formData.timingPeriod || !formData.thumbnail || !formData.category) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please fill in all required fields',
          confirmButtonColor: '#ef4444'
        });
        return;
      }

      // Upload image to Cloudinary
      const imageData = await uploadToCloudinary(formData.thumbnail);
      
      if (!imageData.secure_url) {
        throw new Error('Failed to upload image');
      }

      // Prepare exam data
      const examData = {
        name: formData.examName,
        description: formData.description,
        category: formData.category,
        batchType: formData.batchType,
        fee: Number(formData.examFee),
        timingPeriod: Number(formData.timingPeriod),
        thumbnail: imageData.secure_url
      };

      // Send API request
      const response = await sendApiRequest('api/admin/exams', examData, 'POST');

      if (response.status) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Exam created successfully',
          confirmButtonColor: '#2563eb'
        }).then(() => {
          navigate('/admin-panel/exam-list');
        });
        
        // Reset form
        setFormData({
          examName: '',
          description: '',
          category: '',
          batchType: 'Regular Batch',
          examFee: '',
          timingPeriod: '',
          thumbnail: null
        });
        setPreviewImage(null);
      } else {
        throw new Error(response.message || 'Failed to create exam');
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to create exam',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200"
        >
          <motion.div 
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mb-8 sm:mb-12"
          >
            <div className="bg-blue-50 p-6 rounded-2xl shadow-md border border-gray-200 rotate-3 hover:rotate-6 transition-transform duration-300">
              <FaGraduationCap className="text-blue-600 w-10 h-10" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Add New Exam
              </h1>
              <p className="text-gray-600 mt-2 text-base sm:text-lg">Create a new examination with ease</p>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaBook className="text-blue-500" />
                Exam Name
              </label>
              <input
                type="text"
                name="examName"
                value={formData.examName}
                onChange={handleChange}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-lg bg-white text-gray-900"
                placeholder="Enter exam title"
                required
              />
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaBook className="text-blue-500" />
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-base bg-white text-gray-900 resize-none h-20 sm:h-28"
                placeholder="Enter exam description"
                required
              />
            </motion.div>

            {/* Category Selection */}
            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaBook className="text-blue-500" />
                Category
              </label>
              {categoriesLoading ? (
                <div className="animate-pulse p-4 bg-gray-100 rounded-lg">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : (
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-base bg-white text-gray-900 appearance-none"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              )}
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaUsers className="text-blue-500" />
                Batch Type
              </label>
              <select
                name="batchType"
                value={formData.batchType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-base bg-white text-gray-900 appearance-none"
                required
              >
                <option value="Regular Batch">Regular Batch</option>
                <option value="Weekend Batch">Weekend Batch</option>
              </select>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaClock className="text-blue-500" />
                Duration (days)
              </label>
              <input
                type="number"
                name="timingPeriod"
                value={formData.timingPeriod}
                onChange={handleChange}
                min="1"
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-lg bg-white text-gray-900"
                placeholder="Enter exam duration in days"
                required
              />
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaRupeeSign className="text-blue-500" />
                Exam Fee
              </label>
              <input
                type="number"
                name="examFee"
                value={formData.examFee}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-lg bg-white text-gray-900"
                placeholder="Enter exam fee"
                required
              />
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.02 }} 
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
            >
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                <FaImage className="text-blue-500" />
                Exam Thumbnail
              </label>
              <div className="relative">
                {previewImage && (
                  <div className="mb-4">
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  </div>
                )}
                <input
                  type="file"
                  name="thumbnail"
                  accept="image/*"
                  onChange={handleChange}
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-300 transition-all duration-300 text-base bg-white text-gray-900"
                  required
                />
              </div>
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              type="submit"
              disabled={loading || categoriesLoading}
              className="w-full bg-blue-600 text-white rounded-xl py-4 px-8 text-lg font-semibold hover:bg-blue-700 transition-all duration-300 flex items-center justify-center gap-4 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <FaArrowRight className="text-xl" />
              )}
              {loading ? 'Creating Exam...' : 'Add Exam'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddExam;